import { Box, CardMedia, Icon, MenuItem, Select, Typography } from '@mui/material'
import AdxInput from 'components/custom/AdxInput'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import { format } from 'date-fns'
import React, { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { createInterview, editInterview } from 'services/api/modules/recruitment/scheduler'
import { decimalToTimestamp } from 'services/helper/timestampFormatter'
import useOpenClose from 'services/hooks/useOpenClose'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AdxButton from 'components/custom/AdxButton'

const InterviewDetailsForm = ({
  setIsFirstPage,
  selectedEvent,
  timeSlotValue,
  candidateId,
  candidateName,
  candidateEmail,
  candidateImage,
  interviewerList,
  interviewData,
  isEdit,
}) => {
  const queryClient = useQueryClient()

  const interviewSuccessModalState = useOpenClose(false)

  const navigate = useNavigate()
  const [interviewDetails, setInterviewDetails] = useState({
    title: isEdit ? interviewData?.title : null,
    description: isEdit ? interviewData?.description : null,
    date: selectedEvent.date,
    start: selectedEvent.start,
    end: selectedEvent.end,
    interview_mode: interviewData?.interview_mode ?? 'offline',
    location: isEdit ? interviewData?.location : null,
  })

  const handleBack = () => {
    setIsFirstPage(true)
  }

  const startTime = timeSlotValue.find((item) => item.value === selectedEvent.start).time
  const endTime = timeSlotValue.find((item) => item.value === selectedEvent.end).time

  const { mutate, error } = useMutation(
    async (data) => {
      if (isEdit) {
        return await editInterview(interviewData.id, data)
      } else {
        return await createInterview(data)
      }
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('listCalendar')
        await queryClient.invalidateQueries('getInterviewDetail')
        interviewSuccessModalState.handleOpen()
      },
    },
    {
      onError: (e) => {
        console.error(e)
      },
    }
  )

  const titleError = error?.data?.errors?.title[0]

  const handleSchedule = () => {
    const currentDate = new Date(interviewDetails.date)
    const tomorrow = new Date(currentDate)
    const tomorrowDate = tomorrow.setDate(currentDate.getDate() + 1)

    const processedData = {
      job_ad_candidate_id: candidateId,
      title: interviewDetails.title,
      start_date: format(currentDate, 'yyyy-MM-dd'),
      start_time: decimalToTimestamp(interviewDetails.start),
      end_date: format(interviewDetails.end === 25 ? tomorrowDate : currentDate, 'yyyy-MM-dd'),
      end_time: decimalToTimestamp(interviewDetails.end),
      // location: 'This is location',
      ...(!interviewDetails.description ? {} : { description: interviewDetails.description }),
      // url: 'This is url',
      interviewer: interviewerList.map((item) => ({
        type: item.type === 'External Guest' ? 'External_guest' : 'Staff',
        ...(item.type === 'External Guest' ? { email: item.email } : { attendable_id: item.id }),
      })),
      interview_mode: interviewDetails.interview_mode,
      ...(!interviewDetails.location ? {} : { location: interviewDetails.location }),
    }
    mutate(processedData)
  }

  return (
    <Box
      sx={{ backgroundColor: 'white.main', borderInline: '1px solid #E5E5E5', minHeight: '100%' }}
    >
      <Box
        sx={{
          padding: '24px',
          paddingBottom: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        {/* Candidate */}
        <Box>
          <Typography sx={{ marginBottom: '8px' }}>Candidate</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              backgroundColor: 'default-background.main',
              borderRadius: '4.5px',
              paddingX: '14px',
              paddingY: '16px',
            }}
          >
            {candidateImage ? (
              <CardMedia
                component="img"
                sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '100%',
                  objectFit: 'cover',
                }}
                src={candidateImage}
              />
            ) : (
              <Box
                sx={{
                  height: '40px',
                  width: '40px',
                  borderRadius: '100%',
                  backgroundColor: 'alternate.main',
                  color: 'white.main',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textTransform: 'capitalize',
                }}
              >
                {candidateName.charAt(0)}
              </Box>
            )}
            <Box>
              <Typography>{candidateName}</Typography>
              <Typography sx={{ color: 'light-gray-text.main', fontSize: '12px', fontWeight: 400 }}>
                {candidateEmail}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Interviewer */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}
          >
            <Typography>Interviewer</Typography>
            <Typography
              onClick={handleBack}
              sx={{ textDecoration: 'underline', color: 'alternate.main', cursor: 'pointer' }}
            >
              Edit
            </Typography>
          </Box>
          {interviewerList.map((i, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', gap: '14px', paddingX: '14px', paddingY: '12px' }}
            >
              {i.type === 'External Guest' ? (
                <Box
                  sx={{
                    backgroundColor: 'default-background.main',
                    color: 'alternate.main',
                    borderRadius: '100%',
                    height: '40px',
                    width: '40px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Icon>mail_outlined</Icon>
                </Box>
              ) : i.profile_photo_url ? (
                <CardMedia
                  component="img"
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '100%',
                    objectFit: 'cover',
                  }}
                  src={i.profile_photo_url}
                />
              ) : (
                <Box
                  sx={{
                    backgroundColor: 'alternate.main',
                    color: 'white.main',
                    borderRadius: '100%',
                    height: '40px',
                    width: '40px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textTransform: 'capitalize',
                  }}
                >
                  {i.name.charAt(0) ?? '-'}
                </Box>
              )}
              <Box sx={{ width: '250px' }}>
                <Typography>
                  {i.type === 'External Guest' ? 'External guest' : i.name ?? '-'}
                </Typography>
                <Typography
                  sx={{ fontSize: '12px', fontWeight: 400, color: 'light-gray-text.main' }}
                >
                  {i.email}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Date & Time */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
            }}
          >
            <Typography>Date and time</Typography>
            <Typography
              onClick={handleBack}
              sx={{ color: 'alternate.main', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Edit
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Typography
              sx={{
                width: '50%',
                paddingX: '14px',
                paddingY: '12px',
                borderRadius: '4.5px',
                border: '1px solid #E5E5E5',
                backgroundColor: 'default-background.main',
              }}
            >
              {format(selectedEvent.date, 'EEEE, dd/MM/yyyy')}
            </Typography>
            <Box sx={{ width: '50%', display: 'flex', gap: '16px', alignItems: 'center' }}>
              <Typography
                noWrap
                sx={{
                  width: '100%',
                  paddingX: '14px',
                  paddingY: '12px',
                  borderRadius: '4.5px',
                  border: '1px solid #E5E5E5',
                  backgroundColor: 'default-background.main',
                }}
              >
                {startTime}
              </Typography>
              <Typography>to</Typography>
              <Typography
                noWrap
                sx={{
                  width: '100%',
                  paddingX: '14px',
                  paddingY: '12px',
                  borderRadius: '4.5px',
                  border: '1px solid #E5E5E5',
                  backgroundColor: 'default-background.main',
                }}
              >
                {endTime}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Interview Mode, , Location, Title & Description*/}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography>Interview mode</Typography>
            <Select
              value={interviewDetails?.interview_mode}
              IconComponent={ExpandMoreIcon}
              defaultValue="offline"
              onChange={(e) =>
                setInterviewDetails((prev) => ({
                  title: prev.title,
                  description: prev.description,
                  date: prev.date,
                  start: prev.start,
                  end: prev.end,
                  interview_mode: e.target.value,
                  location: prev.location,
                }))
              }
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                maxWidth: '25%',
              }}
              renderValue={() => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {interviewDetails?.interview_mode === 'offline' ? 'Offline' : 'Online'}
                  </Typography>
                </Box>
              )}
            >
              <MenuItem sx={{ margin: 1, borderRadius: '4.5px' }} value="online">
                Online
              </MenuItem>
              <MenuItem sx={{ margin: 1, borderRadius: '4.5px' }} value="offline">
                Offline
              </MenuItem>
            </Select>
          </Box>
          <AdxInput
            label="Location"
            value={interviewDetails?.location}
            onChange={(e) =>
              setInterviewDetails((prev) => ({
                title: prev.title,
                description: prev.description,
                date: prev.date,
                start: prev.start,
                end: prev.end,
                interview_mode: prev.interview_mode,
                location: e.target.value,
              }))
            }
            placeholder="Add venue or location"
          />
          <AdxInput
            label={
              <React.Fragment>
                Title<span style={{ color: '#09A3A9' }}>*</span>
              </React.Fragment>
            }
            value={interviewDetails?.title}
            onChange={(e) =>
              setInterviewDetails((prev) => ({
                title: e.target.value,
                description: prev.description,
                date: prev.date,
                start: prev.start,
                end: prev.end,
                interview_mode: prev.interview_mode,
                location: prev.location,
              }))
            }
            placeholder="E.g. Interview session for.."
            isErrorShown={titleError}
            errorMessage={<Typography sx={{ color: 'error.main' }}>{titleError}</Typography>}
          />
          <AdxInput
            label="Description"
            value={interviewDetails?.description}
            onChange={(e) =>
              setInterviewDetails((prev) => ({
                title: prev.title,
                description: e.target.value,
                date: prev.date,
                start: prev.start,
                end: prev.end,
                interview_mode: prev.interview_mode,
                location: prev.location,
              }))
            }
            placeholder="Any notes for all participants reference ( Job details, interview details, etc)"
            charLimit={300}
            multiline
            rows={4}
            sxInput={{ paddingY: 1.5 }}
          />
        </Box>
      </Box>
      {interviewSuccessModalState.isOpen && (
        <InterviewSucessModal
          open={interviewSuccessModalState.isOpen}
          onClose={interviewSuccessModalState.handleClose}
          // isLoading={updateCandidateIsLoading}
          onClick={() => navigate(-1)}
          // isError={updateCandidateIsError}
        />
      )}
      {/* Back & Schedule Button */}
      <Box
        sx={{
          width: '100%',
          position: 'fixed',
          bottom: 0,
          left: 0,
          paddingX: '32px',
          paddingTop: '20px',
          paddingBottom: '30px',
          borderTop: '1px solid #E5E5E5',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
          bgcolor: 'primary.light',
        }}
      >
        <AdxButton variant={'outlined'} onClick={handleBack} sx={{ width: '100px' }}>
          Back
        </AdxButton>
        <AdxButton variant={'contained'} onClick={handleSchedule} sx={{ width: '200px' }}>
          Schedule
        </AdxButton>
      </Box>
    </Box>
  )
}

export default InterviewDetailsForm
const InterviewSucessModal = ({ open, onClose, isLoading, onClick, isError }) => (
  <AdxInfoModal
    modalWidth={600}
    muiIcon="business_center"
    open={open}
    // onClose={onClose}
    iconColor="alternate.main"
    title="Interview has been scheduled."
    subtitle="We have invited all participants and sent them the details of the interview via an email."
    footerButtons
    firstBtnLabel={'Got it'}
    firstBtnHandle={onClick}
  >
    {isError && (
      <Typography variant="body1" color="error.main">
        An error has occured. Please try again
      </Typography>
    )}
  </AdxInfoModal>
)
